import { useState, useEffect, useCallback, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Box, Button, Chip, Container, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, MenuItem, Select, Stack, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import Iconify from '../../../components/iconify';
import { useProject } from '../../../hooks/ProjectContext';
import { exportToCsv } from './RegistryUtils';

const COLUMNS = [
  { key: 'raised_at', label: 'Date' },
  { key: 'raised_by', label: 'Raised By' },
  { key: 'category', label: 'Category' },
  { key: 'description', label: 'Description' },
  { key: 'status', label: 'Status' },
];

function fmtDate(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function GrievancesPage() {
  const { selectedProject } = useProject();
  const [rows, setRows] = useState([]);
  const [dlgOpen, setDlgOpen] = useState(false);
  const [form, setForm] = useState({});
  const projectId = selectedProject?.id;

  const load = useCallback(() => {
    if (!projectId) return;
    fetch(`/v1/grievances?projectId=${projectId}`)
      .then(r => r.ok ? r.json() : []).then(setRows).catch(() => setRows([]));
  }, [projectId]);

  useEffect(() => { load(); }, [load]);

  const handleSave = async () => {
    const method = form.id ? 'PUT' : 'POST';
    const url = form.id ? `/v1/grievances/${form.id}` : '/v1/grievances';
    await fetch(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...form, projectId }) });
    setDlgOpen(false);
    load();
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this grievance entry?')) return;
    await fetch(`/v1/grievances/${id}`, { method: 'DELETE' });
    load();
  };

  return (
    <>
      <Helmet><title>Grievance Log | TERRRA</title></Helmet>
      <Container maxWidth="lg" sx={{ py: 2 }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <Typography variant="h5">Grievance Log</Typography>
          <Chip label={`${rows.filter(r => r.status === 'open').length} open`} size="small" color="warning" variant="outlined" />
          <Box sx={{ flex: 1 }} />
          <Button size="small" variant="outlined" startIcon={<Iconify icon="eva:download-outline" />}
            onClick={() => exportToCsv('grievances.csv', COLUMNS, rows.map(r => ({ ...r, raised_at: fmtDate(r.raised_at), description: r.description?.substring(0, 200) })))}>
            Export
          </Button>
          <Button size="small" variant="contained" startIcon={<Iconify icon="eva:plus-fill" />}
            onClick={() => { setForm({ category: 'general', status: 'open' }); setDlgOpen(true); }}>
            Log Grievance
          </Button>
        </Stack>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Grievances received via email to <strong>grievance@terrra.one</strong> are automatically logged here.
          You can also manually add entries.
        </Typography>

        <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Raised By</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Category</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Description</TableCell>
                <TableCell sx={{ fontWeight: 700, fontSize: 12 }}>Status</TableCell>
                <TableCell sx={{ width: 80 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(r => (
                <TableRow key={r.id} hover>
                  <TableCell sx={{ fontSize: 11, whiteSpace: 'nowrap' }}>{fmtDate(r.raised_at)}</TableCell>
                  <TableCell sx={{ fontSize: 12 }}>{r.raised_by}</TableCell>
                  <TableCell><Chip label={r.category} size="small" sx={{ fontSize: 10, height: 18 }} /></TableCell>
                  <TableCell sx={{ fontSize: 12, maxWidth: 300 }}>
                    <Typography variant="caption" noWrap title={r.description}>{r.description?.substring(0, 120)}</Typography>
                  </TableCell>
                  <TableCell>
                    <Select size="small" value={r.status || 'open'}
                      onChange={async e => {
                        await fetch(`/v1/grievances/${r.id}`, {
                          method: 'PUT', headers: { 'Content-Type': 'application/json' },
                          body: JSON.stringify({ status: e.target.value, resolved_at: e.target.value === 'resolved' ? new Date().toISOString() : null }),
                        });
                        load();
                      }}
                      sx={{ fontSize: 10, height: 22 }}>
                      <MenuItem value="open">Open</MenuItem>
                      <MenuItem value="in_progress">In Progress</MenuItem>
                      <MenuItem value="resolved">Resolved</MenuItem>
                      <MenuItem value="closed">Closed</MenuItem>
                    </Select>
                  </TableCell>
                  <TableCell>
                    <IconButton size="small" onClick={() => { setForm(r); setDlgOpen(true); }}>
                      <Iconify icon="eva:edit-outline" sx={{ width: 16, height: 16 }} />
                    </IconButton>
                    <IconButton size="small" onClick={() => handleDelete(r.id)}>
                      <Iconify icon="eva:trash-2-outline" sx={{ width: 16, height: 16, color: 'error.main' }} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {rows.length === 0 && (
                <TableRow><TableCell colSpan={6}>
                  <Typography variant="caption" color="text.secondary">No grievances logged.</Typography>
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Container>

      <Dialog open={dlgOpen} onClose={() => setDlgOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{form.id ? 'Edit' : 'Log'} Grievance</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 0.5 }}>
            <TextField size="small" label="Raised by" fullWidth value={form.raised_by || ''}
              onChange={e => setForm(f => ({ ...f, raised_by: e.target.value }))} />
            <Select size="small" value={form.category || 'general'} fullWidth
              onChange={e => setForm(f => ({ ...f, category: e.target.value }))}>
              <MenuItem value="general">General</MenuItem>
              <MenuItem value="environmental">Environmental</MenuItem>
              <MenuItem value="social">Social</MenuItem>
              <MenuItem value="land_rights">Land Rights</MenuItem>
              <MenuItem value="safety">Safety</MenuItem>
              <MenuItem value="email">Email (auto)</MenuItem>
            </Select>
            <TextField size="small" label="Description" multiline rows={4} fullWidth value={form.description || ''}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))} />
            {form.id && (
              <TextField size="small" label="Response / Resolution" multiline rows={2} fullWidth value={form.response || ''}
                onChange={e => setForm(f => ({ ...f, response: e.target.value }))} />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDlgOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={!form.description}>Save</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
